import React, { createContext, useContext } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { Pressable } from './primitives/motion';
import { useControllable } from '../../hooks/useControllable';
import styles from './Radio.module.css';

// ── CVA ──────────────────────────────────────────────────────────────────────

const radio = cva(styles.label);

// ── Context for Radio.Group ──────────────────────────────────────────────────

interface RadioGroupContext {
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  optionType?: 'default' | 'button';
}

const GroupCtx = createContext<RadioGroupContext | null>(null);

// ── Radio ────────────────────────────────────────────────────────────────────

interface RadioProps extends Omit<React.HTMLAttributes<HTMLLabelElement>, 'onChange'> {
  /** Controlled checked */
  checked?: boolean;
  /** Change handler */
  onChange?: (checked: boolean) => void;
  /** Disabled */
  disabled?: boolean;
  /** Value for group */
  value?: string;
  children?: React.ReactNode;
}

/**
 * Radio — single selection from a group.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | checked | boolean | — | Controlled state |
 * | onChange | (checked) => void | — | Change handler |
 * | disabled | boolean | false | Disable |
 * | value | string | — | Value for Radio.Group |
 *
 * @example
 * <Radio.Group value={val} onChange={setVal}>
 *   <Radio value="a">Option A</Radio>
 *   <Radio value="b">Option B</Radio>
 * </Radio.Group>
 */
export function Radio({
  checked: controlledChecked,
  onChange,
  disabled = false,
  value,
  children,
  style,
  className,
  ...props
}: RadioProps) {
  const group = useContext(GroupCtx);
  const shouldReduce = useReducedMotion() ?? false;

  const isGrouped = group !== null;
  const isChecked = isGrouped ? group.value === value : (controlledChecked ?? false);
  const isDisabled = disabled || (group?.disabled ?? false);
  const isButton = group?.optionType === 'button';

  const handleClick = () => {
    if (isDisabled) return;
    if (isGrouped && value !== undefined) {
      group.onChange?.(value);
    } else {
      onChange?.(!isChecked);
    }
  };

  // Button style variant
  if (isButton) {
    return (
      <label
        className={clsx(
          styles.buttonLabel,
          isChecked && styles.buttonChecked,
          isDisabled && styles.buttonDisabled,
          className,
        )}
        style={style}
        onClick={handleClick}
        {...props}
      >
        <input
          type="radio"
          checked={isChecked}
          readOnly
          className={styles.hiddenInput}
        />
        {children}
      </label>
    );
  }

  // Default dot style
  return (
    <label
      className={clsx(radio({ className }), isDisabled && styles.disabled)}
      style={style}
      onClick={handleClick}
      {...props}
    >
      <Pressable effect="ring" disabled={isDisabled}>
        <span
          className={clsx(styles.ring, isChecked && styles.ringChecked)}
        >
          <AnimatePresence>
            {isChecked && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                transition={
                  shouldReduce
                    ? { duration: 0 }
                    : { type: 'spring', stiffness: 500, damping: 25 }
                }
                className={styles.dot}
              />
            )}
          </AnimatePresence>
        </span>
      </Pressable>
      <input
        type="radio"
        checked={isChecked}
        readOnly
        className={styles.hiddenInput}
      />
      {children && <span>{children}</span>}
    </label>
  );
}

// ── Radio.Group ──────────────────────────────────────────────────────────────

interface RadioOption {
  label: React.ReactNode;
  value: string;
  disabled?: boolean;
}

interface RadioGroupProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Controlled value */
  value?: string;
  /** Initial value (uncontrolled) */
  defaultValue?: string;
  /** Options array (alternative to children) */
  options?: (string | RadioOption)[];
  /** Change handler */
  onChange?: (value: string) => void;
  /** Disabled */
  disabled?: boolean;
  /** Button style instead of dot */
  optionType?: 'default' | 'button';
  /** Layout direction */
  direction?: 'horizontal' | 'vertical';
  children?: React.ReactNode;
}

/**
 * Radio.Group — single selection from options.
 *
 * @example
 * <Radio.Group options={['A', 'B', 'C']} defaultValue="A" />
 * <Radio.Group optionType="button" options={['Day', 'Week', 'Month']} />
 */
function RadioGroup({
  value: controlledValue,
  defaultValue,
  options,
  onChange,
  disabled = false,
  optionType = 'default',
  direction = 'horizontal',
  children,
  style,
  className,
  ...props
}: RadioGroupProps) {
  const [value, setValue] = useControllable(controlledValue, defaultValue ?? '', onChange);

  const normalizedOptions = options?.map((opt) => (typeof opt === 'string' ? { label: opt, value: opt } : opt));

  const ctx: RadioGroupContext = { value, onChange: setValue, disabled, optionType };

  const isButton = optionType === 'button';

  return (
    <GroupCtx.Provider value={ctx}>
      <div
        className={clsx(
          styles.group,
          direction === 'vertical'
            ? (isButton ? styles.groupButtonColumn : styles.groupColumn)
            : (isButton ? styles.groupButtonRow : styles.groupRow),
          className,
        )}
        style={style}
        {...props}
      >
        {normalizedOptions
          ? normalizedOptions.map((opt) => (
              <Radio key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </Radio>
            ))
          : children}
      </div>
    </GroupCtx.Provider>
  );
}

// ── Radio.Button (alias) ─────────────────────────────────────────────────────

const RadioButton = Radio;

Radio.Group = RadioGroup;
Radio.Button = RadioButton;
